// src/features/streams/components/StreamActionsGroup.tsx

import { Group, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconSettings, IconCode, IconBroadcast } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { StreamEncoderSetupModal } from './StreamEncoderSetupModal';
import { PublishModal } from '@/shared/components/PublishModal';
import { LiveAdInjectorModal } from './LiveAdInjectorModal';
import type { StreamViewData, StreamLiveStatus } from '@/lib/types/Stream';
import type { IBackendLiveData } from '@/lib/types/Customer';

interface StreamActionsGroupProps {
  stream: StreamViewData;
  liveStatus: StreamLiveStatus;
  backendMap: Map<number, IBackendLiveData>;
}

export function StreamActionsGroup({ stream, liveStatus, backendMap }: StreamActionsGroupProps) {
  const [encoderOpened, { open: openEncoder, close: closeEncoder }] = useDisclosure();
  const [publishOpened, { open: openPublish, close: closePublish }] = useDisclosure();
  const [adsOpened, { open: openAds, close: closeAds }] = useDisclosure();
  const { selectedCustomer } = useAuth();

  const allowAds = selectedCustomer?.cfg.quota.features.account.allowAds ?? false;
  const isLive = liveStatus === 'live' || liveStatus === 'recording';

  return (
    <>
      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconSettings size={16} />}
          onClick={openEncoder}
        >
          Encoder Setup
        </Button>

        <Button
          variant="light"
          leftSection={<IconCode size={16} />}
          onClick={openPublish}
        >
          Publish
        </Button>

        {allowAds && (
          <Button
            variant="light"
            leftSection={<IconBroadcast size={16} />}
            disabled={!isLive}
            onClick={openAds}
          >
            Inject Ads
          </Button>
        )}
      </Group>

      <StreamEncoderSetupModal
        opened={encoderOpened}
        onClose={closeEncoder}
        stream={stream}
        backendMap={backendMap}
      />

      <PublishModal
        opened={publishOpened}
        onClose={closePublish}
        media={stream}
      />

      {allowAds && (
        <LiveAdInjectorModal
          opened={adsOpened}
          onClose={closeAds}
          mediaId={stream.media.mediaId}
          isLive={isLive}
        />
      )}
    </>
  );
}
